import React, { useRef, useEffect, useCallback, useContext, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress, Avatar, Box } from "@mui/material";
import useUserSearch from "../../utils/hooks/useUserSearch";
import { initials, hueFor } from "../AssigneeGroup/utils";

// ─── Per-instance pagination channel ─────────────────────────────────────────
// The listbox renders inside the Autocomplete's Popper (a DOM portal), but React
// context flows through portals. Each picker publishes its OWN { onLoadMore,
// hasMore } here. The context value is a ref object with a STABLE identity, so
// the IntersectionObserver subscribes exactly once and still reads the newest
// handler at fire time.
//
// This replaces module-level singletons, which were only correct for a single
// mounted picker — AddTodoTaskModal mounts three, so whichever rendered last won
// and the other two paginated its cursor.
const ListboxChannel = React.createContext(null);

// Defined at module level so React never sees a new component type — the listbox
// is never unmounted/remounted and the IntersectionObserver stays live.
const InfiniteListbox = React.forwardRef(function InfiniteListbox({ children, ...props }, ref) {
  const channel = useContext(ListboxChannel);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const container = sentinel.parentElement; // the <ul>, which is the scroller
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        const ch = channel?.current;
        if (ch?.hasMore) ch.onLoadMore?.();
      },
      { root: container, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [channel]);

  return (
    <ul ref={ref} {...props}>
      {children}
      <li aria-hidden="true" ref={sentinelRef} style={{ height: "1px", listStyle: "none", padding: 0, margin: 0 }} />
    </ul>
  );
});

const LOAD_MORE_SENTINEL = "__assignee_load_more__";

/**
 * AssigneePicker — SINGLE-select member picker.
 *
 * For picking several people use AssigneeSelect: an avatar stack plus a ⊕ that
 * opens a search popover. Chips-in-an-input does not scale past two or three
 * names, which is why this component has no multi mode.
 *
 * Props:
 *   value           – selected user _id string (or null/undefined)
 *   onChange        – (id: string | null, option: Option | null) => void
 *   preloadedOption – { _id, label } shown immediately before the list loads
 *   roles           – comma-separated role names to restrict results
 *   disabled | error | helperText | placeholder | size
 */
const AssigneePicker = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  placeholder = "Search and select assignee",
  size = "small",
  roles,
  preloadedOption,
}) => {
  const { users, loadingInit, loadingMore, hasMore, search, loadMore } = useUserSearch({ roles });

  // A server-side search REPLACES `users`, so without this the picked option
  // would lose its label the moment someone typed.
  const cache = useRef(new Map());
  useEffect(() => {
    if (preloadedOption?._id) cache.current.set(String(preloadedOption._id), preloadedOption);
    users.forEach((u) => cache.current.set(u._id, u));
  }, [preloadedOption, users]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const key = String(value);
    return (
      users.find((u) => u._id === key) ||
      // Read the PROP during render, and keep it in the deps. Resolving this
      // only through the effect-populated cache above was a bug: a caller that
      // sets `value` and `preloadedOption` in the SAME commit (the assignee
      // auto-fill does) renders before that effect runs, and falls back to
      // `users` — which holds only the first page. Anyone found by searching is
      // outside it, so the field drew empty and never recovered.
      (preloadedOption?._id && String(preloadedOption._id) === key ? preloadedOption : null) ||
      cache.current.get(key) ||
      null
    );
  }, [value, users, preloadedOption]);

  // Publish this instance's pagination handler on its own channel.
  const channelRef = useRef({ onLoadMore: null, hasMore: false });
  channelRef.current.onLoadMore = loadMore;
  channelRef.current.hasMore = hasMore;

  const handleChange = useCallback(
    (_, v) => {
      if (v?._id) cache.current.set(v._id, v);
      onChange(v?._id ?? null, v ?? null);
    },
    [onChange],
  );

  const options = loadingMore ? [...users, { _id: LOAD_MORE_SENTINEL, label: "" }] : users;

  return (
    <ListboxChannel.Provider value={channelRef}>
      <Autocomplete
        fullWidth
        size={size}
        disabled={disabled}
        options={options}
        loading={loadingInit}
        value={selectedOption}
        onChange={handleChange}
        getOptionLabel={(o) => (o?._id === LOAD_MORE_SENTINEL ? "" : o?.label || "")}
        getOptionKey={(o) => o?._id}
        getOptionDisabled={(o) => o?._id === LOAD_MORE_SENTINEL}
        isOptionEqualToValue={(a, b) => a?._id === b?._id}
        filterOptions={(x) => x}
        ListboxComponent={InfiniteListbox}
        // Closing discards the typed text, so the list must go back to unfiltered
        // too — otherwise reopening shows an empty box over the last search's results.
        onClose={() => search("")}
        onInputChange={(_, val, reason) => {
          // "clear" (the ✕ button) must also drop the filter, or the list stays
          // stuck on the last search.
          if (reason === "input" || reason === "clear") search(val);
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          if (option._id === LOAD_MORE_SENTINEL) {
            return (
              <li key={key} {...rest} style={{ justifyContent: "center", padding: "8px 0", cursor: "default" }}>
                <CircularProgress size={16} />
              </li>
            );
          }
          return (
            <li key={key} {...rest}>
              <Avatar
                src={option.file || undefined}
                sx={{
                  width: 22, height: 22, mr: 1, fontSize: "9px", fontWeight: 700,
                  bgcolor: hueFor(option._id), color: "#fff",
                }}
              >
                {initials(option.label)}
              </Avatar>
              <Box component="span" sx={{ fontSize: "13px" }}>{option.label}</Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingInit && <CircularProgress size={14} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </ListboxChannel.Provider>
  );
};

export default AssigneePicker;
